import { SubscriptionCreationDialogBoxComponent } from './subscription-creation-dialog/subscription-creation-dialog-box.component';
import { CommonModule } from '@angular/common';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbDropdownModule,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import {
  Component,
  Injector,
  OnInit,
  Signal,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import {
  CreateUpdateCustomerInvoiceDto,
  CustomerDocDefaultValueDto,
  CustomerInvoiceDto,
  CustomerInvoiceService,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { CustomerService, PointOfSaleService } from '@digiwall/goliath-contacts/proxy';
import { Observable, asyncScheduler, firstValueFrom, map, of, scheduled } from 'rxjs';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { GoliathContactsEnumerationTypeStore, SalesPersonStore } from '@digiwall/goliath-contacts';
import { AnnexesListComponent, GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ClassificationTypeService, DocRole } from '@digiwall/goliath-core/proxy';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { CustomerInvoiceLinesEditorComponent } from './customer-invoice-lines/customer-invoice-lines-editor.component';
import { CustomerInvoicePaymentsComponent } from './customer-invoice-payments/customer-invoice-payments.component';
import { ReportViewerComponent } from '@odyssey/reporting';
import { FieldMapping } from '../../base-third-party-docs/base-third-party-doc-detail';
import { CustomerInvoicePermission, DocReportPermission } from '@digiwall/goliath-documents/config';
import {
  CounterComponent,
  DropdownMenu,
  DropdownMenuComponent,
  PanelComponent,
} from '@digiwall/ui-kit';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import {
  ChatComponent,
  DeactivatableComponentService,
  DwNavLinkDirective,
  HistoryComponent,
  OdysseyOffcanvas,
} from '@digiwall/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DwKeyboardShortcutDirective } from '@digiwall/core';
import { DwScrollSpyDirective } from '@digiwall/ag-grid';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocCustomerAddressesComponent } from '../../base-docs/detail/doc-customer-addresses/doc-customer-addresses.component';
import { DocCustomerOthersComponent } from '../../base-docs/detail/doc-customer-others/doc-customer-others.component';
import { AsyncPipe } from '@angular/common';
import { SidePanelComponent } from '@digiwall/ui-kit';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { InvoiceCancellationService } from '../services/invoice-cancellation.service';
import { DocSaveRequestContext } from '../../base-docs/detail/doc-save-request-context';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  BaseThirdPartyDocLineUpdatedDto,
  DocumentCoreCardComponent,
  DocumentCustomerCardComponent,
  DocumentCustomerDateCardComponent,
  DocumentCustomerStatusCardComponent,
} from '../../../components';
import { SubscriptionFeatureDefinition } from '@digiwall/goliath-core/config';
import { BaseInvoiceDocDetail } from '../../base-invoice-docs/base-invoice-doc-detail';
import { DocNumberTagComponent } from '../../../components/doc-number-tag/doc-number-tag.component';

@Component({
  selector: 'document-customer-invoice-detail',
  templateUrl: './customer-invoice-detail.component.html',
  styleUrl: './customer-invoice-detail.component.scss',
  providers: [DocTemplateService],
  imports: [
    CommonModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    CustomerInvoiceLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocCustomerAddressesComponent,
    DocCustomerOthersComponent,
    AnnexesListComponent,
    NgbDropdownModule,
    DropdownMenuComponent,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    DialogBoxComponent,
    ReportViewerComponent,
    AsyncPipe,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentCustomerCardComponent,
    DocumentCustomerDateCardComponent,
    DocumentCustomerStatusCardComponent,
    HistoryComponent,
    DocNumberTagComponent,
  ],
})
export class CustomerInvoiceDetailComponent
  extends BaseInvoiceDocDetail<
    CustomerInvoiceService,
    CustomerInvoiceDto,
    CreateUpdateCustomerInvoiceDto,
    CustomerDocDefaultValueDto
  >
  implements OnInit
{
  @ViewChild('customerInvoiceLineGrid') docLineGrid: CustomerInvoiceLinesEditorComponent;
  @ViewChild('report') reportViewer: ReportViewerComponent;

  docRole = DocRole.CustomerInvoice;

  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  contactEnumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  salesPersonStore = inject(SalesPersonStore);
  protected invoiceCancellationService = inject(InvoiceCancellationService);

  disabled = false;

  protected saveActionBtns = computed(() => {
    let btns = [
      {
        label: this.localizationService.instant('::Save'),
        action: () => this.save(),
        disabled: () => !this.form.dirty,
      },
    ];
    return btns;
  });

  actions: Signal<DropdownMenu> = computed(() => ({
    groups: [
      this.baseActionsThirdPartyDoc().concat(
        {
          label: '::GeneratePdf',
          icon: 'digi-out-pdf-file',
          action: () => this.generatePdf(this.reportViewer),
        },
        {
          label: 'Doc::PrintDocument',
          icon: 'digi-out-print',
          action: () => this.printDocument(),
          enabled: this.canPrintDocument,
        },
        {
          label: 'Doc::CustomerInvoice:AddPayment',
          icon: 'digi-out-payment-method',
          action: () => this.addPayment(),
        },
        {
          label: 'Doc::CustomerInvoice:Cancel',
          icon: 'digi-out-arrow-back-circle',
          action: () => this.cancelInvoice(),
        },
        {
          label: 'Doc::CustomerInvoice:CreateSubscription',
          icon: 'digi-out-add-file',
          feature: SubscriptionFeatureDefinition,
          action: () => this.createSubscription(),
        },
        {
          label: 'Doc::CustomerInvoice:GenerateUbl',
          icon: 'digi-out-invoice',
          action: () => {
            return firstValueFrom(this.docService.createUBLFile(this.entity.id));
          },
        },
        {
          label: 'Doc::CustomerInvoice:Reopen',
          icon: 'digi-out-unlock',
          action: () => {
            this.confirmationService
              .warn('Doc::CustomerInvoice:ReopenConfirmation', '::AreYouSure')
              .subscribeOnce(x => {
                if (x != Confirmation.Status.confirm) return;
                this.customerInvoiceService
                  .reopen(this.docId)
                  .subscribeOnce(() => this.getEntity(this.docId));
              });
          },
          visible: computed(() =>
            this.permissionHelper.checkPermission(CustomerInvoicePermission.Override),
          ),
        },
      ),
      [
        {
          label: 'Doc::GenerateDocPrintTemplate',
          icon: 'digi-out-edit-square',
          action: () => this.openDesigner(),
          requiredPolicy: DocReportPermission.Create,
        },
      ],
    ],
  }));

  totalAmount: number = 0;
  totalAmountVatIncluded: number = 0;
  isDueDateExpired = false;

  override get defaultValue() {
    return Object.assign(this.baseDefaultValue, {
      isExported: false,
      allowPartialDelivery: false,
    });
  }

  remainingAmount$: Observable<number>;

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    protected customerInvoiceService: CustomerInvoiceService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    injector: Injector,
    customerService: CustomerService,
    config: ConfigStateService,
    docTemplateService: DocTemplateService,
    classificationTypeService: ClassificationTypeService,
    confirmationService: ConfirmationService,
    protected pointOfSaleService: PointOfSaleService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      customerInvoiceService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      docTemplateService,
      injector,
      customerService,
      config,
      confirmationService,
      deactivableComponentService,
    );

    effect(() => {
      const textTemplates = this.textTemplates();
      const queryParams = this.route.snapshot.queryParams;
      if (queryParams['textTemplateId'] && textTemplates.length > 0) {
        const textTemplate = textTemplates.find(t => t.id === queryParams['textTemplateId']);
        if (textTemplate) {
          this.sendTextTemplate(textTemplate);

          // remove the query param after consumption
          this.router.navigate(['.'], {
            replaceUrl: true,
            relativeTo: this.route,
          });
        }
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }

  addPayment() {
    if (this.form.get('isBlocked').value) {
      this.isBlockedError();
      return;
    }
    if (this.form.get('litigation').value) {
      this.isLitigationError();
      return;
    }

    this.formStateService.checkDirtyForm(this.form, () => {
      let canvaRef = this.offcanvasService.open(CustomerInvoicePaymentsComponent, null, {
        position: 'end',
        panelClass: 'w-100 w-sm-75',
      });

      (canvaRef.componentInstance as CustomerInvoicePaymentsComponent).invoiceId = this.entity.id;
      (canvaRef.componentInstance as CustomerInvoicePaymentsComponent).service.set(
        this.customerInvoiceService,
      );
      (canvaRef.componentInstance as CustomerInvoicePaymentsComponent).readonly.set(
        this.form.get('isExported').value,
      );

      canvaRef.dismissed.subscribe(() => {
        this.getEntity(this.entity.id);
      });
    });
  }

  protected cancelInvoice() {
    this.invoiceCancellationService.cancel(this.entity, this.route);
  }

  override get formModel() {
    return Object.assign({}, super.formModel, {
      dueDate: [null, Validators.required],
      isExported: [false, Validators.required],
      invoicingAddress_ThirdPartyAddressId: [null],
      vcs: [null],
    });
  }

  private createSubscription(): void {
    const modalRef = this.modalService.open(SubscriptionCreationDialogBoxComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      scrollable: true,
      injector: this.injector,
      size: 'lg',
    });

    modalRef.componentInstance.invoice.set(this.entity);
    modalRef.componentInstance.lineIds.set(this.docLineGrid.SelectedLines().map(line => line.id));

    modalRef.closed.subscribe((id?: string) => {
      if (id != null) {
        this.router.navigate(['../../subscriptions/', id], {
          relativeTo: this.route,
        });
      }
    });
  }

  protected override initFormObservers() {
    super.initFormObservers();

    this.form.get('paymentTermId').valueChanges.subscribe(paymentTermId => {
      const documentDate = this.form.get('documentDate')?.value;
      this.computeDueDate(documentDate, paymentTermId);
    });
  }

  protected computeDueDate(documentDate?: any, paymentTermId?: any) {
    if (documentDate == null) return;

    this.docService
      .calculateDueDate({
        docDate: documentDate,
        paymentTermId: paymentTermId,
      })
      .subscribe(dueDate => {
        this.form.get('dueDate').setValue(dueDate.date);
      });
  }

  protected override disableForm(): void {
    super.disableForm();
    this.form.get('description').enable();
  }

  protected override afterSave(): void {
    this.isDueDateExpired = this.entity.dueDate < new Date().toISOString();
    super.afterSave();
    this.fetchAccountingData(this.docId, this.entity.thirdPartyId);
  }

  getEntityCallback(): void {
    super.getEntityCallback();
    super.loadThirdParties(this.entity.isClosed);

    this.docLineGrid?.lineGrid?.grid?.grid?.api?.redrawRows();
  }

  protected fetchAccountingData(docId: string, thirdPartyId: string): void {
    this.setRemainingAmount$(docId);
    this.isDueDateExpired = this.entity.dueDate < new Date().toISOString();
    super.fetchAccountingData(docId, thirdPartyId);
  }

  private setRemainingAmount$(docId: string) {
    if (this.entity.balanceDue != null) {
      this.remainingAmount$ = scheduled([this.entity.balanceDue], asyncScheduler);
    } else if (
      !this.config.getGlobalFeatureIsEnabled('Goliath.Accounting') ||
      this.entity.externalReference == null
    ) {
      this.remainingAmount$ = this.docService.getRemainingAmount(this.entity.id);
    } else {
      this.remainingAmount$ = this.docService
        .getAccountingInvoiceOutstanding(docId)
        .pipe(map(response => response?.openAmount));
    }
  }

  protected override onThirdPartyChanged = (thirdPartyId: string): void => {
    if (this.isClosed()) return;

    super.onThirdPartyChanged(thirdPartyId);

    if (thirdPartyId == null) return;
    this.thirdPartyId$.next(thirdPartyId);
  };

  protected override getFieldMappings(): FieldMapping[] {
    return [
      ...super.getFieldMappings(),
      { formField: 'representativeCommision', customerField: 'representativeCommission' },
    ];
  }

  protected override refreshAfterLineUpdate(params: Partial<BaseThirdPartyDocLineUpdatedDto>) {
    super.refreshAfterLineUpdate(params);
    if (params.balanceDue != null) {
      this.entity.balanceDue = params.balanceDue;
      this.setRemainingAmount$(this.docId);
    }
  }
}
